import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { formatDate as formatDateUtil, formatDateTime } from '../utils/dateFormat';
import { 
  MessageSquare, Mail, History, Search, X, Users, Plus, Send, Clock, 
  CheckCircle, XCircle, AlertCircle, Loader2, ChevronRight, Eye, 
  Calendar, Trash2, Edit, RefreshCw, FileText, Palette, Lock, Unlock,
  Monitor, Smartphone, Phone
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Recipient chip component
const RecipientChip = ({ recipient, onRemove }) => {
  const statusColors = {
    valid: 'bg-green-100 text-green-800 border-green-200',
    invalid: 'bg-red-100 text-red-800 border-red-200',
    opted_out: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  };
  
  return (
    <div className={cn(
      "inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs border",
      statusColors[recipient.validation_status] || 'bg-gray-100 text-gray-800 border-gray-200'
    )}>
      <span className="max-w-[150px] truncate">
        {recipient.participant_name || recipient.contact_value}
      </span>
      {recipient.validation_status === 'invalid' && (
        <AlertCircle className="h-3 w-3" />
      )}
      {onRemove && (
        <button onClick={() => onRemove(recipient)} className="hover:text-red-600">
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  );
};

// Composer component for SMS/Email
const MessageComposer = ({ type, onSend }) => {
  const { token } = useAuth();
  const [step, setStep] = useState(1); // 1: Recipients, 2: Compose, 3: Preview, 4: Confirm
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [selectedEnrollmentTypes, setSelectedEnrollmentTypes] = useState([]);
  const [enrollmentTypeRecipients, setEnrollmentTypeRecipients] = useState([]);
  const [manualRecipients, setManualRecipients] = useState('');
  const [message, setMessage] = useState('');
  const [subject, setSubject] = useState('');
  const [sendNow, setSendNow] = useState(true);
  const [scheduledDate, setScheduledDate] = useState(null);
  const [scheduledTime, setScheduledTime] = useState('09:00');
  const [enrollmentTypes, setEnrollmentTypes] = useState([]);
  const [mergeFields, setMergeFields] = useState([]);
  const [validation, setValidation] = useState(null);
  const [previews, setPreviews] = useState([]);
  const [sending, setSending] = useState(false);
  
  // SMS-specific state
  const [outboundNumbers, setOutboundNumbers] = useState([]);
  const [selectedFromNumber, setSelectedFromNumber] = useState('');

  // Fetch enrollment types, merge fields, and outbound numbers on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const requests = [
          axios.get(`${API_URL}/api/enrollment-types`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API_URL}/api/mass-communications/merge-fields`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ];
        
        // Fetch outbound numbers for SMS
        if (type === 'sms') {
          requests.push(
            axios.get(`${API_URL}/api/mass-communications/outbound-numbers`, {
              headers: { Authorization: `Bearer ${token}` }
            })
          );
        }
        
        const responses = await Promise.all(requests);
        setEnrollmentTypes(responses[0].data.enrollment_types || []);
        setMergeFields(responses[1].data.merge_fields || []);
        
        if (type === 'sms' && responses[2]) {
          const numbers = responses[2].data.outbound_numbers || [];
          setOutboundNumbers(numbers);
          // Auto-select if only one number, or select default
          if (numbers.length === 1) {
            setSelectedFromNumber(numbers[0].phone_number);
          } else {
            const defaultNum = numbers.find(n => n.is_default);
            if (defaultNum) {
              setSelectedFromNumber(defaultNum.phone_number);
            }
          }
        }
      } catch (err) {
        console.error('Failed to fetch data:', err);
      }
    };
    fetchData();
  }, [token, type]);

  // Search participants
  const searchParticipants = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      const res = await axios.get(`${API_URL}/api/mass-communications/participants/search`, {
        params: { q: query, communication_type: type, limit: 15 },
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchResults(res.data.participants || []);
    } catch (err) {
      console.error('Search failed:', err);
    }
  }, [token, type]);

  useEffect(() => {
    const debounce = setTimeout(() => searchParticipants(searchQuery), 300);
    return () => clearTimeout(debounce);
  }, [searchQuery, searchParticipants]);

  // Fetch recipients by enrollment type
  const fetchEnrollmentTypeRecipients = async (typeIds) => {
    if (!typeIds.length) {
      setEnrollmentTypeRecipients([]);
      return;
    }
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/mass-communications/recipients/by-enrollment-type`, {
        params: { enrollment_type_ids: typeIds.join(','), communication_type: type },
        headers: { Authorization: `Bearer ${token}` }
      });
      setEnrollmentTypeRecipients(res.data.recipients || []);
    } catch (err) {
      console.error('Failed to fetch enrollment recipients:', err);
      toast.error('Failed to load recipients');
    } finally {
      setLoading(false);
    }
  };

  const handleEnrollmentTypeChange = (typeId, checked) => {
    const newTypes = checked 
      ? [...selectedEnrollmentTypes, typeId]
      : selectedEnrollmentTypes.filter(t => t !== typeId);
    setSelectedEnrollmentTypes(newTypes);
    fetchEnrollmentTypeRecipients(newTypes);
  };

  const addParticipant = (participant) => {
    if (selectedParticipants.find(p => p.participant_id === participant.participant_id)) {
      toast.info('Participant already added');
      return;
    }
    setSelectedParticipants([...selectedParticipants, participant]);
    setSearchQuery('');
    setSearchResults([]);
  };

  const removeParticipant = (participant) => {
    setSelectedParticipants(selectedParticipants.filter(
      p => p.participant_id !== participant.participant_id
    ));
  };

  const insertMergeField = (field) => {
    setMessage(prev => prev + field);
  };

  // Validate before preview
  const validateAndPreview = async () => {
    // Check from_number for SMS
    if (type === 'sms' && !selectedFromNumber) {
      toast.error('Please select a Send From number');
      return;
    }
    
    const manualList = manualRecipients
      .split(/[\n,;]+/)
      .map(r => r.trim())
      .filter(r => r);

    try {
      setLoading(true);
      const res = await axios.post(`${API_URL}/api/mass-communications/preview`, {
        communication_type: type,
        subject: type === 'email' ? subject : null,
        message,
        selected_participants: selectedParticipants,
        selected_enrollment_types: selectedEnrollmentTypes,
        manual_recipients: manualList,
        send_immediately: sendNow,
        scheduled_datetime: !sendNow && scheduledDate 
          ? `${format(scheduledDate, 'yyyy-MM-dd')}T${scheduledTime}:00` 
          : null,
        from_number: type === 'sms' ? selectedFromNumber : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setValidation(res.data);
      setPreviews(res.data.previews || []);
      
      if (res.data.is_valid) {
        setStep(3);
      } else {
        toast.error(res.data.errors?.join(', ') || 'Validation failed');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Validation failed');
    } finally {
      setLoading(false);
    }
  };

  // Send communication
  const handleSend = async () => {
    const manualList = manualRecipients
      .split(/[\n,;]+/)
      .map(r => r.trim())
      .filter(r => r);

    try {
      setSending(true);
      const res = await axios.post(`${API_URL}/api/mass-communications/send`, {
        communication_type: type,
        subject: type === 'email' ? subject : null,
        message,
        selected_participants: selectedParticipants,
        selected_enrollment_types: selectedEnrollmentTypes,
        manual_recipients: manualList,
        send_immediately: sendNow,
        scheduled_datetime: !sendNow && scheduledDate 
          ? `${format(scheduledDate, 'yyyy-MM-dd')}T${scheduledTime}:00` 
          : null,
        scheduled_timezone: 'America/Chicago',
        from_number: type === 'sms' ? selectedFromNumber : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(res.data.message);
      onSend?.(res.data);
      
      // Reset form
      setStep(1);
      setSelectedParticipants([]);
      setSelectedEnrollmentTypes([]);
      setEnrollmentTypeRecipients([]);
      setManualRecipients('');
      setMessage('');
      setSubject('');
      setValidation(null);
      setPreviews([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  // Calculate totals
  const allRecipients = [
    ...selectedParticipants.map(p => ({ ...p, source: 'individual' })),
    ...enrollmentTypeRecipients.map(r => ({ ...r, source: 'enrollment' }))
  ];
  
  // Dedupe by contact value
  const uniqueContacts = new Set();
  const dedupedRecipients = allRecipients.filter(r => {
    if (!r.contact_value || uniqueContacts.has(r.contact_value)) return false;
    uniqueContacts.add(r.contact_value);
    return true;
  });

  const validCount = dedupedRecipients.filter(r => r.validation_status === 'valid').length;
  const invalidCount = dedupedRecipients.filter(r => r.validation_status === 'invalid').length;
  const optedOutCount = dedupedRecipients.filter(r => r.validation_status === 'opted_out').length;
  const manualCount = manualRecipients.split(/[\n,;]+/).filter(r => r.trim()).length;

  return (
    <div className="space-y-6">
      {/* Progress indicator */}
      <div className="flex items-center gap-2 text-sm">
        <div className={cn("flex items-center gap-1", step >= 1 ? "text-primary" : "text-muted-foreground")}>
          <div className={cn("w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium",
            step >= 1 ? "bg-primary text-primary-foreground" : "bg-muted")}>1</div>
          <span>Recipients</span>
        </div>
        <ChevronRight className="h-4 w-4 text-muted-foreground" />
        <div className={cn("flex items-center gap-1", step >= 2 ? "text-primary" : "text-muted-foreground")}>
          <div className={cn("w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium",
            step >= 2 ? "bg-primary text-primary-foreground" : "bg-muted")}>2</div>
          <span>Compose</span>
        </div>
        <ChevronRight className="h-4 w-4 text-muted-foreground" />
        <div className={cn("flex items-center gap-1", step >= 3 ? "text-primary" : "text-muted-foreground")}>
          <div className={cn("w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium",
            step >= 3 ? "bg-primary text-primary-foreground" : "bg-muted")}>3</div>
          <span>Preview</span>
        </div>
        <ChevronRight className="h-4 w-4 text-muted-foreground" />
        <div className={cn("flex items-center gap-1", step >= 4 ? "text-primary" : "text-muted-foreground")}>
          <div className={cn("w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium",
            step >= 4 ? "bg-primary text-primary-foreground" : "bg-muted")}>4</div>
          <span>Send</span>
        </div>
      </div>

      {/* Step 1: Recipients */}
      {step === 1 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Selection methods */}
          <div className="space-y-4">
            {/* Individual search */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Search className="h-4 w-4" />
                  Search Participants
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="relative">
                  <Input
                    placeholder="Search by name, ID, or State ID..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-background border rounded-md shadow-lg max-h-60 overflow-auto">
                      {searchResults.map((p) => (
                        <button
                          key={p.participant_id}
                          onClick={() => addParticipant(p)}
                          className="w-full px-3 py-2 text-left hover:bg-muted flex items-center justify-between text-sm"
                        >
                          <div>
                            <div className="font-medium">{p.participant_name}</div>
                            <div className="text-xs text-muted-foreground">
                              {p.participant_number} {p.state_id_number && `| ${p.state_id_number}`}
                            </div>
                          </div>
                          {p.validation_status === 'invalid' && (
                            <Badge variant="destructive" className="text-xs">No {type === 'sms' ? 'Phone' : 'Email'}</Badge>
                          )}
                          {p.validation_status === 'opted_out' && (
                            <Badge variant="warning" className="text-xs">Opted Out</Badge>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Enrollment type selection */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Select by Enrollment Type
                </CardTitle>
                <CardDescription className="text-xs">
                  Select enrollment types to include all participants with active enrollments
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-48">
                  <div className="space-y-2">
                    {enrollmentTypes.map((et) => (
                      <label key={et.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted p-2 rounded">
                        <input
                          type="checkbox"
                          checked={selectedEnrollmentTypes.includes(et.id)}
                          onChange={(e) => handleEnrollmentTypeChange(et.id, e.target.checked)}
                          className="rounded border-gray-300"
                        />
                        <span>{et.name}</span>
                        {et.state && <Badge variant="outline" className="text-xs">{et.state}</Badge>}
                      </label>
                    ))}
                  </div>
                </ScrollArea>
                {loading && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mt-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading recipients...
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Manual entry */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Plus className="h-4 w-4" />
                  Manual Recipients
                </CardTitle>
                <CardDescription className="text-xs">
                  Enter {type === 'sms' ? 'phone numbers' : 'email addresses'} (one per line or comma-separated)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Textarea
                  placeholder={type === 'sms' ? '555-123-4567\n555-234-5678' : 'email@example.com\nanother@example.com'}
                  value={manualRecipients}
                  onChange={(e) => setManualRecipients(e.target.value)}
                  rows={3}
                />
              </CardContent>
            </Card>
          </div>

          {/* Right: Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium">Recipient Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Selected participants */}
              {selectedParticipants.length > 0 && (
                <div>
                  <Label className="text-xs text-muted-foreground">Individually Selected ({selectedParticipants.length})</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {selectedParticipants.map((p) => (
                      <RecipientChip key={p.participant_id} recipient={p} onRemove={removeParticipant} />
                    ))}
                  </div>
                </div>
              )}

              {/* Enrollment type recipients */}
              {enrollmentTypeRecipients.length > 0 && (
                <div>
                  <Label className="text-xs text-muted-foreground">
                    From Enrollment Types ({enrollmentTypeRecipients.length})
                  </Label>
                  <div className="text-sm mt-1">
                    {selectedEnrollmentTypes.map(typeId => {
                      const et = enrollmentTypes.find(e => e.id === typeId);
                      const count = enrollmentTypeRecipients.filter(r => r.enrollment_type_id === typeId).length;
                      return (
                        <div key={typeId} className="flex items-center justify-between py-1">
                          <span>{et?.name || typeId}</span>
                          <Badge variant="secondary">{count}</Badge>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Manual count */}
              {manualCount > 0 && (
                <div>
                  <Label className="text-xs text-muted-foreground">Manual Recipients</Label>
                  <div className="text-sm mt-1">{manualCount} {type === 'sms' ? 'phone numbers' : 'email addresses'}</div>
                </div>
              )}

              {/* Stats */}
              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Valid Recipients</span>
                  <span className="font-medium text-green-600">{validCount}</span>
                </div>
                {invalidCount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Invalid (no contact)</span>
                    <span className="font-medium text-red-600">{invalidCount}</span>
                  </div>
                )}
                {optedOutCount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Opted Out</span>
                    <span className="font-medium text-yellow-600">{optedOutCount}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm font-medium border-t pt-2">
                  <span>Total to Send</span>
                  <span>{validCount + manualCount}</span>
                </div>
              </div>

              <Button 
                className="w-full" 
                onClick={() => setStep(2)}
                disabled={validCount + manualCount === 0}
              >
                Continue to Compose
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Step 2: Compose */}
      {step === 2 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            {type === 'email' && (
              <div>
                <Label>Subject</Label>
                <Input
                  placeholder="Enter email subject..."
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                />
              </div>
            )}

            {/* Send From selector for SMS */}
            {type === 'sms' && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium">Send From</CardTitle>
                  <CardDescription className="text-xs">
                    Select the phone number to send SMS messages from
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {outboundNumbers.length === 0 ? (
                    <div className="text-sm text-muted-foreground">
                      No outbound numbers configured. Contact an administrator.
                    </div>
                  ) : (
                    <Select value={selectedFromNumber} onValueChange={setSelectedFromNumber}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select outbound number..." />
                      </SelectTrigger>
                      <SelectContent>
                        {outboundNumbers.map((num) => (
                          <SelectItem key={num.phone_number} value={num.phone_number}>
                            <div className="flex items-center gap-2">
                              <span>{num.phone_number}</span>
                              {num.label && <span className="text-muted-foreground">({num.label})</span>}
                              {num.is_default && <Badge variant="outline" className="text-xs ml-1">Default</Badge>}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </CardContent>
              </Card>
            )}

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Message</Label>
                {type === 'sms' && (
                  <span className="text-xs text-muted-foreground">
                    {message.length} / 160 characters
                    {message.length > 160 && ` (${Math.ceil(message.length / 160)} segments)`}
                  </span>
                )}
              </div>
              <Textarea
                placeholder="Type your message here..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={type === 'sms' ? 4 : 10}
                className={type === 'email' ? 'min-h-[200px]' : ''}
              />
            </div>

            {/* Send timing */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Send Timing</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={sendNow}
                      onChange={() => setSendNow(true)}
                      className="text-primary"
                    />
                    <Send className="h-4 w-4" />
                    <span>Send Now</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={!sendNow}
                      onChange={() => setSendNow(false)}
                      className="text-primary"
                    />
                    <Clock className="h-4 w-4" />
                    <span>Schedule for Later</span>
                  </label>
                </div>

                {!sendNow && (
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <Label className="text-xs">Date</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full justify-start text-left font-normal">
                            <Calendar className="mr-2 h-4 w-4" />
                            {scheduledDate ? formatDateUtil(scheduledDate) : 'Select date'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <CalendarComponent
                            mode="single"
                            selected={scheduledDate}
                            onSelect={setScheduledDate}
                            disabled={(date) => date < new Date()}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div className="w-32">
                      <Label className="text-xs">Time</Label>
                      <Input
                        type="time"
                        value={scheduledTime}
                        onChange={(e) => setScheduledTime(e.target.value)}
                      />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Merge fields sidebar */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">Merge Fields</CardTitle>
              <CardDescription className="text-xs">
                Click to insert personalized fields
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {mergeFields.map((mf) => (
                  <button
                    key={mf.field}
                    onClick={() => insertMergeField(mf.field)}
                    className="w-full text-left px-3 py-2 text-sm rounded-md hover:bg-muted border"
                  >
                    <div className="font-mono text-xs text-primary">{mf.field}</div>
                    <div className="text-xs text-muted-foreground">{mf.description}</div>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Action buttons */}
          <div className="lg:col-span-3 flex justify-between">
            <Button variant="outline" onClick={() => setStep(1)}>
              Back to Recipients
            </Button>
            <Button onClick={validateAndPreview} disabled={loading || !message.trim()}>
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Preview & Validate
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      )}

      {/* Step 3: Preview */}
      {step === 3 && validation && (
        <div className="space-y-6">
          {/* Send From info for SMS */}
          {type === 'sms' && selectedFromNumber && (
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-4 flex items-center gap-3">
                <Phone className="h-5 w-5 text-blue-600" />
                <div>
                  <div className="text-sm font-medium">Sending From</div>
                  <div className="text-sm text-muted-foreground">{selectedFromNumber}</div>
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Validation summary */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-green-600">{validation.summary.valid_recipients}</div>
                <div className="text-xs text-muted-foreground">Valid Recipients</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-red-600">{validation.summary.invalid_recipients}</div>
                <div className="text-xs text-muted-foreground">Invalid</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-yellow-600">{validation.summary.opted_out_recipients}</div>
                <div className="text-xs text-muted-foreground">Opted Out</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-blue-600">{validation.summary.duplicates_removed}</div>
                <div className="text-xs text-muted-foreground">Duplicates Removed</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold">{validation.summary.total_recipients}</div>
                <div className="text-xs text-muted-foreground">Total</div>
              </CardContent>
            </Card>
          </div>

          {/* Warnings */}
          {validation.warnings?.length > 0 && (
            <Card className="border-yellow-200 bg-yellow-50">
              <CardContent className="pt-4">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                  <div>
                    <div className="font-medium text-yellow-800">Warnings</div>
                    {validation.warnings.map((w, i) => (
                      <div key={i} className="text-sm text-yellow-700">{w}</div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Sample previews */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium">Sample Message Previews</CardTitle>
              <CardDescription>How your message will appear to recipients</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {previews.map((preview, i) => (
                  <div key={i} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-sm">{preview.recipient_name}</span>
                      <span className="text-xs text-muted-foreground">{preview.contact_value}</span>
                    </div>
                    {type === 'email' && preview.resolved_subject && (
                      <div className="text-sm mb-2">
                        <span className="text-muted-foreground">Subject: </span>
                        {preview.resolved_subject}
                      </div>
                    )}
                    <div className="text-sm bg-muted p-3 rounded whitespace-pre-wrap">
                      {preview.resolved_message}
                    </div>
                    {preview.unresolved_fields?.length > 0 && (
                      <div className="text-xs text-yellow-600 mt-2">
                        Unresolved fields: {preview.unresolved_fields.join(', ')}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Action buttons */}
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(2)}>
              Back to Edit
            </Button>
            <Button onClick={() => setStep(4)}>
              Confirm & {sendNow ? 'Send' : 'Schedule'}
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      )}

      {/* Step 4: Confirm */}
      {step === 4 && (
        <Card className="max-w-lg mx-auto">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              {sendNow ? <Send className="h-6 w-6 text-primary" /> : <Clock className="h-6 w-6 text-primary" />}
            </div>
            <CardTitle>
              {sendNow ? 'Ready to Send' : 'Ready to Schedule'}
            </CardTitle>
            <CardDescription>
              {sendNow 
                ? `You are about to send this ${type.toUpperCase()} to ${validation?.summary.valid_recipients} recipients now.`
                : `You are about to schedule this ${type.toUpperCase()} for ${scheduledDate ? formatDateUtil(scheduledDate) : ''} at ${scheduledTime} for ${validation?.summary.valid_recipients} recipients.`
              }
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-center">
              <div className="text-3xl font-bold text-primary">{validation?.summary.valid_recipients}</div>
              <div className="text-sm text-muted-foreground">Recipients</div>
            </div>
            {type === 'sms' && selectedFromNumber && (
              <div className="text-center pt-2 border-t">
                <div className="text-xs text-muted-foreground">Sending from</div>
                <div className="font-medium">{selectedFromNumber}</div>
              </div>
            )}
          </CardContent>
          <div className="p-6 pt-0 flex gap-4">
            <Button variant="outline" className="flex-1" onClick={() => setStep(3)}>
              Go Back
            </Button>
            <Button className="flex-1" onClick={handleSend} disabled={sending}>
              {sending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {sendNow ? 'Send Now' : 'Schedule'}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
};

// History component
const CommunicationHistory = () => {
  const { token } = useAuth();
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [filter, setFilter] = useState({ type: '', status: '' });

  const fetchBatches = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filter.type) params.communication_type = filter.type;
      if (filter.status) params.status = filter.status;
      
      const res = await axios.get(`${API_URL}/api/mass-communications/batches`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      setBatches(res.data.batches || []);
    } catch (err) {
      toast.error('Failed to load history');
    } finally {
      setLoading(false);
    }
  }, [token, filter]);

  useEffect(() => {
    fetchBatches();
  }, [fetchBatches]);

  const fetchBatchDetails = async (batchId) => {
    try {
      const res = await axios.get(`${API_URL}/api/mass-communications/batches/${batchId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedBatch(res.data);
    } catch (err) {
      toast.error('Failed to load batch details');
    }
  };

  const cancelBatch = async (batchId) => {
    if (!window.confirm('Are you sure you want to cancel this scheduled communication?')) return;
    try {
      await axios.delete(`${API_URL}/api/mass-communications/batches/${batchId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Batch cancelled');
      fetchBatches();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel');
    }
  };

  const statusBadge = (status) => {
    const styles = {
      sent: 'bg-green-100 text-green-800',
      partially_sent: 'bg-yellow-100 text-yellow-800',
      failed: 'bg-red-100 text-red-800',
      scheduled: 'bg-blue-100 text-blue-800',
      processing: 'bg-purple-100 text-purple-800',
      cancelled: 'bg-gray-100 text-gray-800',
    };
    return <Badge className={styles[status] || 'bg-gray-100'}>{status}</Badge>;
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex gap-4">
        <Select value={filter.type || "all"} onValueChange={(v) => setFilter(f => ({ ...f, type: v === "all" ? "" : v }))}>
          <SelectTrigger className="w-32">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="sms">SMS</SelectItem>
            <SelectItem value="email">Email</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filter.status || "all"} onValueChange={(v) => setFilter(f => ({ ...f, status: v === "all" ? "" : v }))}>
          <SelectTrigger className="w-32">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="partially_sent">Partially Sent</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="scheduled">Scheduled</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="icon" onClick={fetchBatches}>
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      {/* Batch list */}
      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : batches.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            No communication batches found
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {batches.map((batch) => (
            <Card key={batch.id} className="hover:bg-muted/50 cursor-pointer" onClick={() => fetchBatchDetails(batch.id)}>
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={cn(
                      "w-10 h-10 rounded-full flex items-center justify-center",
                      batch.communication_type === 'sms' ? 'bg-blue-100' : 'bg-purple-100'
                    )}>
                      {batch.communication_type === 'sms' ? (
                        <MessageSquare className="h-5 w-5 text-blue-600" />
                      ) : (
                        <Mail className="h-5 w-5 text-purple-600" />
                      )}
                    </div>
                    <div>
                      <div className="font-medium text-sm">{batch.id}</div>
                      <div className="text-xs text-muted-foreground">
                        {batch.created_by_name} • {batch.created_at ? formatDateTime(batch.created_at) : ''}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="text-sm font-medium">{batch.recipient_count} recipients</div>
                      <div className="text-xs text-muted-foreground">
                        {batch.successful_count > 0 && <span className="text-green-600">{batch.successful_count} sent</span>}
                        {batch.failed_count > 0 && <span className="text-red-600 ml-2">{batch.failed_count} failed</span>}
                      </div>
                    </div>
                    {statusBadge(batch.status)}
                    {batch.status === 'scheduled' && (
                      <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); cancelBatch(batch.id); }}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Batch detail dialog */}
      <Dialog open={!!selectedBatch} onOpenChange={() => setSelectedBatch(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Batch Details - {selectedBatch?.id}</DialogTitle>
            <DialogDescription>
              {selectedBatch?.communication_type?.toUpperCase()} • {selectedBatch?.created_by_name} • {selectedBatch?.created_at ? formatDateTime(selectedBatch.created_at) : ''}
            </DialogDescription>
          </DialogHeader>
          
          {selectedBatch && (
            <div className="space-y-4">
              {/* Stats */}
              <div className="grid grid-cols-4 gap-4">
                <div className="text-center p-3 bg-muted rounded">
                  <div className="text-xl font-bold">{selectedBatch.recipient_count}</div>
                  <div className="text-xs text-muted-foreground">Total</div>
                </div>
                <div className="text-center p-3 bg-green-50 rounded">
                  <div className="text-xl font-bold text-green-600">{selectedBatch.successful_count}</div>
                  <div className="text-xs text-muted-foreground">Successful</div>
                </div>
                <div className="text-center p-3 bg-red-50 rounded">
                  <div className="text-xl font-bold text-red-600">{selectedBatch.failed_count}</div>
                  <div className="text-xs text-muted-foreground">Failed</div>
                </div>
                <div className="text-center p-3 bg-blue-50 rounded">
                  {statusBadge(selectedBatch.status)}
                </div>
              </div>

              {/* Send From info for SMS batches */}
              {selectedBatch.communication_type === 'sms' && selectedBatch.from_number && (
                <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg">
                  <Phone className="h-4 w-4 text-blue-600" />
                  <span className="text-sm"><strong>Sent From:</strong> {selectedBatch.from_number}</span>
                </div>
              )}

              {/* Message content */}
              <div>
                <Label className="text-xs text-muted-foreground">Message</Label>
                {selectedBatch.subject && (
                  <div className="text-sm mb-1"><strong>Subject:</strong> {selectedBatch.subject}</div>
                )}
                <div className="bg-muted p-3 rounded text-sm whitespace-pre-wrap">{selectedBatch.message}</div>
              </div>

              {/* Recipient results */}
              {selectedBatch.results?.length > 0 && (
                <div>
                  <Label className="text-xs text-muted-foreground mb-2 block">Recipient Results</Label>
                  <ScrollArea className="h-64 border rounded">
                    <div className="p-2 space-y-1">
                      {selectedBatch.results.map((result, i) => (
                        <div key={i} className="flex items-center justify-between py-2 px-3 hover:bg-muted rounded text-sm">
                          <div>
                            <div className="font-medium">{result.recipient?.participant_name || result.contact_value}</div>
                            <div className="text-xs text-muted-foreground">{result.contact_value}</div>
                          </div>
                          <div className="flex items-center gap-2">
                            {result.status === 'sent' || result.status === 'queued' ? (
                              <CheckCircle className="h-4 w-4 text-green-500" />
                            ) : (
                              <XCircle className="h-4 w-4 text-red-500" />
                            )}
                            <span className={result.status === 'sent' || result.status === 'queued' ? 'text-green-600' : 'text-red-600'}>
                              {result.status}
                            </span>
                            {result.error && (
                              <span className="text-xs text-red-500 max-w-[200px] truncate">{result.error}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Designed Email Composer Component
const DesignedEmailComposer = ({ onSend }) => {
  const { token } = useAuth();
  const [step, setStep] = useState(1); // 1: Template, 2: Recipients, 3: Edit, 4: Preview, 5: Confirm
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateData, setTemplateData] = useState(null);
  const [editableContent, setEditableContent] = useState({});
  const [subject, setSubject] = useState('');
  const [preheader, setPreheader] = useState('');
  const [filter, setFilter] = useState({ category: 'all', search: '' });
  
  // Recipients state (same as MessageComposer)
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [selectedEnrollmentTypes, setSelectedEnrollmentTypes] = useState([]);
  const [enrollmentTypeRecipients, setEnrollmentTypeRecipients] = useState([]);
  const [manualRecipients, setManualRecipients] = useState('');
  const [enrollmentTypes, setEnrollmentTypes] = useState([]);
  
  // Send timing
  const [sendNow, setSendNow] = useState(true);
  const [scheduledDate, setScheduledDate] = useState(null);
  const [scheduledTime, setScheduledTime] = useState('09:00');
  
  // Preview
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewMode, setPreviewMode] = useState('desktop');
  const [validation, setValidation] = useState(null);
  const [sending, setSending] = useState(false);

  // Fetch templates and enrollment types on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [tplRes, etRes, catRes] = await Promise.all([
          axios.get(`${API_URL}/api/admin/email-designer/templates?active_only=true`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API_URL}/api/enrollment-types`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API_URL}/api/admin/email-designer/categories`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        setTemplates(tplRes.data.templates || []);
        setEnrollmentTypes(etRes.data.enrollment_types || []);
        setCategories(catRes.data.categories || []);
      } catch (err) {
        console.error('Failed to fetch data:', err);
      }
    };
    fetchData();
  }, [token]);

  // Search participants
  const searchParticipants = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      const res = await axios.get(`${API_URL}/api/mass-communications/participants/search`, {
        params: { q: query, communication_type: 'email', limit: 15 },
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchResults(res.data.participants || []);
    } catch (err) {
      console.error('Search failed:', err);
    }
  }, [token]);

  useEffect(() => {
    const debounce = setTimeout(() => searchParticipants(searchQuery), 300);
    return () => clearTimeout(debounce);
  }, [searchQuery, searchParticipants]);

  // Fetch enrollment type recipients
  const fetchEnrollmentTypeRecipients = async (typeIds) => {
    if (!typeIds.length) {
      setEnrollmentTypeRecipients([]);
      return;
    }
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/mass-communications/recipients/by-enrollment-type`, {
        params: { enrollment_type_ids: typeIds.join(','), communication_type: 'email' },
        headers: { Authorization: `Bearer ${token}` }
      });
      setEnrollmentTypeRecipients(res.data.recipients || []);
    } catch (err) {
      console.error('Failed to fetch enrollment recipients:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEnrollmentTypeChange = (typeId, checked) => {
    const newTypes = checked 
      ? [...selectedEnrollmentTypes, typeId]
      : selectedEnrollmentTypes.filter(t => t !== typeId);
    setSelectedEnrollmentTypes(newTypes);
    fetchEnrollmentTypeRecipients(newTypes);
  };

  const addParticipant = (participant) => {
    if (selectedParticipants.find(p => p.participant_id === participant.participant_id)) {
      toast.info('Participant already added');
      return;
    }
    setSelectedParticipants([...selectedParticipants, participant]);
    setSearchQuery('');
    setSearchResults([]);
  };

  const removeParticipant = (participant) => {
    setSelectedParticipants(selectedParticipants.filter(
      p => p.participant_id !== participant.participant_id
    ));
  };

  // Select template
  const selectTemplate = async (template) => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/admin/email-designer/templates/${template.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplateData(res.data);
      setSelectedTemplate(template);
      setSubject(res.data.default_subject || '');
      setPreheader(res.data.preheader || '');
      
      // Initialize editable content from template blocks
      const editable = {};
      (res.data.blocks || []).forEach(block => {
        if (!block.is_locked) {
          editable[block.id] = { ...block.content };
        }
      });
      setEditableContent(editable);
      setStep(2);
    } catch (err) {
      toast.error('Failed to load template');
    } finally {
      setLoading(false);
    }
  };

  // Calculate totals
  const allRecipients = [
    ...selectedParticipants.map(p => ({ ...p, source: 'individual' })),
    ...enrollmentTypeRecipients.map(r => ({ ...r, source: 'enrollment' }))
  ];
  const uniqueContacts = new Set();
  const dedupedRecipients = allRecipients.filter(r => {
    if (!r.contact_value || uniqueContacts.has(r.contact_value)) return false;
    uniqueContacts.add(r.contact_value);
    return true;
  });
  const validCount = dedupedRecipients.filter(r => r.validation_status === 'valid').length;
  const manualCount = manualRecipients.split(/[\n,;]+/).filter(r => r.trim()).length;

  // Generate preview
  const generatePreview = async () => {
    if (!templateData) return;
    try {
      setLoading(true);
      
      // Apply editable content changes to template
      const modifiedBlocks = templateData.blocks.map(block => {
        if (!block.is_locked && editableContent[block.id]) {
          return { ...block, content: { ...block.content, ...editableContent[block.id] } };
        }
        return block;
      });
      
      const res = await axios.post(`${API_URL}/api/admin/email-designer/templates/${templateData.id}/preview`, {
        merge_data: {}
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPreviewHtml(res.data.html);
      setStep(4);
    } catch (err) {
      toast.error('Failed to generate preview');
    } finally {
      setLoading(false);
    }
  };

  // Send designed email
  const handleSend = async () => {
    const manualList = manualRecipients.split(/[\n,;]+/).map(r => r.trim()).filter(r => r);
    
    try {
      setSending(true);
      const res = await axios.post(`${API_URL}/api/mass-communications/send-designed`, {
        template_id: templateData.id,
        subject: subject,
        preheader: preheader,
        editable_content: editableContent,
        selected_participants: selectedParticipants,
        selected_enrollment_types: selectedEnrollmentTypes,
        manual_recipients: manualList,
        send_immediately: sendNow,
        scheduled_datetime: !sendNow && scheduledDate 
          ? `${format(scheduledDate, 'yyyy-MM-dd')}T${scheduledTime}:00` 
          : null,
        scheduled_timezone: 'America/Chicago'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(res.data.message);
      onSend?.(res.data);
      
      // Reset
      setStep(1);
      setSelectedTemplate(null);
      setTemplateData(null);
      setSelectedParticipants([]);
      setSelectedEnrollmentTypes([]);
      setEnrollmentTypeRecipients([]);
      setManualRecipients('');
      setEditableContent({});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const filteredTemplates = templates.filter(t => {
    if (filter.category !== 'all' && t.category !== filter.category) return false;
    if (filter.search && !t.name.toLowerCase().includes(filter.search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="space-y-6">
      {/* Progress */}
      <div className="flex items-center gap-2 text-sm">
        {[
          { num: 1, label: 'Template' },
          { num: 2, label: 'Recipients' },
          { num: 3, label: 'Edit Content' },
          { num: 4, label: 'Preview' },
          { num: 5, label: 'Send' }
        ].map((s, i) => (
          <React.Fragment key={s.num}>
            {i > 0 && <ChevronRight className="h-4 w-4 text-muted-foreground" />}
            <div className={cn("flex items-center gap-1", step >= s.num ? "text-primary" : "text-muted-foreground")}>
              <div className={cn("w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium",
                step >= s.num ? "bg-primary text-primary-foreground" : "bg-muted")}>{s.num}</div>
              <span className="hidden sm:inline">{s.label}</span>
            </div>
          </React.Fragment>
        ))}
      </div>

      {/* Step 1: Template Selection */}
      {step === 1 && (
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search templates..."
                value={filter.search}
                onChange={(e) => setFilter({ ...filter, search: e.target.value })}
                className="pl-9"
              />
            </div>
            <Select value={filter.category} onValueChange={(v) => setFilter({ ...filter, category: v })}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredTemplates.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Palette className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No templates available</h3>
                <p className="text-muted-foreground">Ask an administrator to create and activate email templates.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredTemplates.map((template) => (
                <Card 
                  key={template.id} 
                  className="cursor-pointer hover:shadow-md hover:border-primary transition-all"
                  onClick={() => selectTemplate(template)}
                >
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">{template.name}</CardTitle>
                    <CardDescription className="text-xs">
                      {categories.find(c => c.id === template.category)?.name || template.category}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {template.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2">{template.description}</p>
                    )}
                    <Button variant="outline" size="sm" className="mt-3 w-full">
                      Use Template
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Step 2: Recipients (same as standard email) */}
      {step === 2 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Search className="h-4 w-4" />
                  Search Participants
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="relative">
                  <Input
                    placeholder="Search by name, ID, or State ID..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-background border rounded-md shadow-lg max-h-60 overflow-auto">
                      {searchResults.map((p) => (
                        <button
                          key={p.participant_id}
                          onClick={() => addParticipant(p)}
                          className="w-full px-3 py-2 text-left hover:bg-muted flex items-center justify-between text-sm"
                        >
                          <div>
                            <div className="font-medium">{p.participant_name}</div>
                            <div className="text-xs text-muted-foreground">{p.participant_number}</div>
                          </div>
                          {p.validation_status === 'invalid' && <Badge variant="destructive" className="text-xs">No Email</Badge>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Select by Enrollment Type
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-48">
                  <div className="space-y-2">
                    {enrollmentTypes.map((et) => (
                      <label key={et.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted p-2 rounded">
                        <input
                          type="checkbox"
                          checked={selectedEnrollmentTypes.includes(et.id)}
                          onChange={(e) => handleEnrollmentTypeChange(et.id, e.target.checked)}
                          className="rounded border-gray-300"
                        />
                        <span>{et.name}</span>
                      </label>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Plus className="h-4 w-4" />
                  Manual Recipients
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  placeholder="email@example.com&#10;another@example.com"
                  value={manualRecipients}
                  onChange={(e) => setManualRecipients(e.target.value)}
                  rows={3}
                />
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium">Recipient Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {selectedParticipants.length > 0 && (
                <div>
                  <Label className="text-xs text-muted-foreground">Individually Selected ({selectedParticipants.length})</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {selectedParticipants.map((p) => (
                      <RecipientChip key={p.participant_id} recipient={p} onRemove={removeParticipant} />
                    ))}
                  </div>
                </div>
              )}

              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Valid Recipients</span>
                  <span className="font-medium text-green-600">{validCount}</span>
                </div>
                <div className="flex justify-between text-sm font-medium border-t pt-2">
                  <span>Total to Send</span>
                  <span>{validCount + manualCount}</span>
                </div>
              </div>

              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
                <Button className="flex-1" onClick={() => setStep(3)} disabled={validCount + manualCount === 0}>
                  Continue to Edit
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Step 3: Edit Editable Content */}
      {step === 3 && templateData && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Email Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label className="text-xs">Subject</Label>
                  <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
                </div>
                <div>
                  <Label className="text-xs">Preheader (Preview Text)</Label>
                  <Input value={preheader} onChange={(e) => setPreheader(e.target.value)} />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Editable Content Areas</CardTitle>
                <CardDescription className="text-xs">
                  You can modify the content below. Locked areas (marked with 🔒) cannot be changed.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[300px] pr-4">
                  {templateData.blocks?.map((block, index) => (
                    <div key={block.id} className={cn(
                      "mb-3 p-3 border rounded-lg",
                      block.is_locked ? "bg-muted/50 border-amber-200" : "bg-white"
                    )}>
                      <div className="flex items-center gap-2 mb-2">
                        {block.is_locked ? <Lock className="h-3 w-3 text-amber-600" /> : <Unlock className="h-3 w-3 text-green-600" />}
                        <span className="text-xs font-medium capitalize">{block.type}</span>
                        {block.is_locked && <Badge variant="outline" className="text-xs">Locked</Badge>}
                      </div>
                      
                      {!block.is_locked && (
                        <div className="space-y-2">
                          {block.type === 'text' && (
                            <Textarea
                              value={editableContent[block.id]?.text || block.content.text || ''}
                              onChange={(e) => setEditableContent({
                                ...editableContent,
                                [block.id]: { ...editableContent[block.id], text: e.target.value }
                              })}
                              rows={3}
                            />
                          )}
                          {(block.type === 'header' || block.type === 'heading') && (
                            <Input
                              value={editableContent[block.id]?.text || block.content.text || ''}
                              onChange={(e) => setEditableContent({
                                ...editableContent,
                                [block.id]: { ...editableContent[block.id], text: e.target.value }
                              })}
                            />
                          )}
                          {block.type === 'button' && (
                            <div className="space-y-2">
                              <Input
                                placeholder="Button text"
                                value={editableContent[block.id]?.text || block.content.text || ''}
                                onChange={(e) => setEditableContent({
                                  ...editableContent,
                                  [block.id]: { ...editableContent[block.id], text: e.target.value }
                                })}
                              />
                              <Input
                                placeholder="Button URL"
                                value={editableContent[block.id]?.url || block.content.url || ''}
                                onChange={(e) => setEditableContent({
                                  ...editableContent,
                                  [block.id]: { ...editableContent[block.id], url: e.target.value }
                                })}
                              />
                            </div>
                          )}
                        </div>
                      )}
                      
                      {block.is_locked && block.content.text && (
                        <p className="text-xs text-muted-foreground truncate">{block.content.text}</p>
                      )}
                    </div>
                  ))}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm">Send Timing</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" checked={sendNow} onChange={() => setSendNow(true)} />
                    <Send className="h-4 w-4" />
                    <span>Send Now</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" checked={!sendNow} onChange={() => setSendNow(false)} />
                    <Clock className="h-4 w-4" />
                    <span>Schedule</span>
                  </label>
                </div>
                {!sendNow && (
                  <div className="flex gap-4">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="flex-1">
                          <Calendar className="mr-2 h-4 w-4" />
                          {scheduledDate ? formatDateUtil(scheduledDate) : 'Select date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <CalendarComponent
                          mode="single"
                          selected={scheduledDate}
                          onSelect={setScheduledDate}
                          disabled={(date) => date < new Date()}
                        />
                      </PopoverContent>
                    </Popover>
                    <Input type="time" value={scheduledTime} onChange={(e) => setScheduledTime(e.target.value)} className="w-32" />
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(2)}>Back</Button>
              <Button className="flex-1" onClick={generatePreview} disabled={loading}>
                {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Preview
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Step 4: Preview */}
      {step === 4 && (
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm">Email Preview</CardTitle>
                <div className="flex items-center gap-2">
                  <Button
                    variant={previewMode === 'desktop' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setPreviewMode('desktop')}
                  >
                    <Monitor className="h-4 w-4 mr-1" /> Desktop
                  </Button>
                  <Button
                    variant={previewMode === 'mobile' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setPreviewMode('mobile')}
                  >
                    <Smartphone className="h-4 w-4 mr-1" /> Mobile
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="mb-4 p-3 bg-muted rounded-lg">
                <div className="text-sm"><strong>Subject:</strong> {subject}</div>
                {preheader && <div className="text-sm text-muted-foreground"><strong>Preheader:</strong> {preheader}</div>}
                <div className="text-sm"><strong>Recipients:</strong> {validCount + manualCount}</div>
              </div>
              <div 
                className={cn(
                  "border rounded-lg overflow-hidden bg-gray-100 mx-auto",
                  previewMode === 'mobile' ? 'max-w-[375px]' : 'max-w-[600px]'
                )}
              >
                {previewHtml ? (
                  <iframe srcDoc={previewHtml} className="w-full h-[400px] border-0" title="Preview" />
                ) : (
                  <div className="flex items-center justify-center h-[400px] text-muted-foreground">
                    <Loader2 className="h-8 w-8 animate-spin" />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setStep(3)}>Back to Edit</Button>
            <Button className="flex-1" onClick={() => setStep(5)}>
              Confirm & {sendNow ? 'Send' : 'Schedule'}
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      )}

      {/* Step 5: Confirm */}
      {step === 5 && (
        <Card className="max-w-lg mx-auto">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              {sendNow ? <Send className="h-6 w-6 text-primary" /> : <Clock className="h-6 w-6 text-primary" />}
            </div>
            <CardTitle>{sendNow ? 'Ready to Send' : 'Ready to Schedule'}</CardTitle>
            <CardDescription>
              {sendNow 
                ? `You are about to send this designed email to ${validCount + manualCount} recipients now.`
                : `You are about to schedule this email for ${scheduledDate ? formatDateUtil(scheduledDate) : ''} at ${scheduledTime}.`
              }
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <div className="text-3xl font-bold text-primary">{validCount + manualCount}</div>
            <div className="text-sm text-muted-foreground">Recipients</div>
          </CardContent>
          <div className="p-6 pt-0 flex gap-4">
            <Button variant="outline" className="flex-1" onClick={() => setStep(4)}>Go Back</Button>
            <Button className="flex-1" onClick={handleSend} disabled={sending}>
              {sending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {sendNow ? 'Send Now' : 'Schedule'}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
};

// Main page component
const CommunicationsPage = () => {
  const [activeTab, setActiveTab] = useState('sms');
  const [emailMode, setEmailMode] = useState('standard'); // 'standard' or 'designed'
  const { hasPermission } = useAuth();

  // Check permissions
  const canAccess = hasPermission('send_communication') || hasPermission('view_communications');

  if (!canAccess) {
    return (
      <div className="p-8 text-center">
        <h1 className="text-2xl font-bold text-foreground mb-2">Access Denied</h1>
        <p className="text-muted-foreground">You do not have permission to access the Communications module.</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Communications</h1>
          <p className="text-muted-foreground">Send mass SMS and Email communications</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-md grid-cols-3">
          <TabsTrigger value="sms" className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            SMS
          </TabsTrigger>
          <TabsTrigger value="email" className="flex items-center gap-2">
            <Mail className="h-4 w-4" />
            Email
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <History className="h-4 w-4" />
            History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="sms" className="mt-6">
          <MessageComposer type="sms" onSend={() => setActiveTab('history')} />
        </TabsContent>

        <TabsContent value="email" className="mt-6">
          {/* Email Mode Toggle */}
          <div className="mb-6">
            <div className="flex items-center gap-4 p-1 bg-muted rounded-lg w-fit">
              <button
                onClick={() => setEmailMode('standard')}
                className={cn(
                  "flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors",
                  emailMode === 'standard' 
                    ? "bg-background shadow text-foreground" 
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                <FileText className="h-4 w-4" />
                Standard Email
              </button>
              <button
                onClick={() => setEmailMode('designed')}
                className={cn(
                  "flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors",
                  emailMode === 'designed' 
                    ? "bg-background shadow text-foreground" 
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                <Palette className="h-4 w-4" />
                Designed Email
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {emailMode === 'standard' 
                ? 'Create a simple rich-text email message.' 
                : 'Use a professionally designed template created by your administrator.'}
            </p>
          </div>

          {emailMode === 'standard' ? (
            <MessageComposer type="email" onSend={() => setActiveTab('history')} />
          ) : (
            <DesignedEmailComposer onSend={() => setActiveTab('history')} />
          )}
        </TabsContent>

        <TabsContent value="history" className="mt-6">
          <CommunicationHistory />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CommunicationsPage;
